import { noop } from 'lodash';
import {
  createContext,
  type MutableRefObject,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { usePortalStore } from '@air/primitive-portal';
import { reportErrorToBugsnag } from '@air/utils-error';

export type FullscreenContextValues = {
  isFullscreen: boolean;
  setIsFullscreen: (isFullscreen: boolean) => void;
  fullscreenContainerRef: MutableRefObject<HTMLDivElement | null>;
  toggleFullscreen: () => void;
};

const DefaultValue: FullscreenContextValues = {
  isFullscreen: false,
  setIsFullscreen: noop,
  fullscreenContainerRef: { current: null },
  toggleFullscreen: noop,
};

export const FullscreenContext = createContext<FullscreenContextValues>(DefaultValue);

type FullscreenProviderProps = {
  children: ReactNode;
};

export const FullscreenContextProvider = memo(({ children }: FullscreenProviderProps) => {
  const [isFullscreen, changeIsFullscreen] = useState(false);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const isFullscreenRef = useRef(isFullscreen);
  const setPortal = usePortalStore((state) => state.setPortal);

  const setIsFullscreen = useCallback((newIsFullscreen: boolean) => {
    try {
      isFullscreenRef.current = newIsFullscreen;
      changeIsFullscreen(newIsFullscreen);
    } catch (error) {
      reportErrorToBugsnag({
        error,
        context: 'Failed to set fullscreen state',
        metadata: {
          fullscreen: {
            targetState: newIsFullscreen,
            currentState: isFullscreenRef.current,
          },
        },
      });
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    try {
      if (fullscreenContainerRef.current) {
        if (isFullscreen) {
          document.exitFullscreen();
          setPortal(null);
        } else {
          fullscreenContainerRef.current.requestFullscreen();
          setPortal(fullscreenContainerRef.current);
        }
      }
    } catch (error) {
      reportErrorToBugsnag({
        error,
        context: 'Failed to toggle fullscreen mode',
        metadata: {
          fullscreenAPI: {
            currentState: isFullscreen,
            containerExists: !!fullscreenContainerRef.current,
            documentFullscreenEnabled: document.fullscreenEnabled,
          },
        },
      });
    }
  }, [isFullscreen, setPortal]);

  // Listen to browser fullscreen changes to keep context in sync
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;

      if (isCurrentlyFullscreen !== isFullscreenRef.current) {
        isFullscreenRef.current = isCurrentlyFullscreen;
        changeIsFullscreen(isCurrentlyFullscreen);
        setPortal(isCurrentlyFullscreen ? fullscreenContainerRef.current : null);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [setPortal]);

  // Handle ESC key to exit fullscreen
  useHotkeys(
    'escape',
    () => {
      if (isFullscreen && document.fullscreenElement) {
        toggleFullscreen();
      }
    },
    {
      enabled: isFullscreen,
      enableOnContentEditable: false,
      enableOnFormTags: false,
      preventDefault: true,
    },
    [isFullscreen, toggleFullscreen],
  );

  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {
          // Ignore errors, the modal is unmounting anyway
        });
      }
    };
  }, []);

  const values = useMemo(() => {
    return {
      isFullscreen,
      setIsFullscreen,
      fullscreenContainerRef,
      toggleFullscreen,
    };
  }, [isFullscreen, setIsFullscreen, toggleFullscreen]);

  return <FullscreenContext.Provider value={values}>{children}</FullscreenContext.Provider>;
});

FullscreenContextProvider.displayName = 'FullscreenContextProvider';

export const useFullscreenContext = () => {
  const values = useContext(FullscreenContext);

  if (values === DefaultValue) {
    console.error('FullscreenContext used outside of FullscreenContextProvider');
  }

  return values;
};
